import { SupabaseClient } from '@supabase/supabase-js'
import { Database, Tables } from "@/supabase/types"
import { LLM, LLMID, OpenRouterLLM } from "@/types/base"
import { getApiKeys, getTenantById, getValidEnvKeysRecords, Metadata } from "@/db/tenants"
import { keyMap, VALID_ENV_KEYS } from "@/types/base/valid-keys"
import { getAllDefinedProviders } from "@/db/providers"
import { getModelsByProviderId as getModelsByProviderId, mapToLLM } from "@/db/models"
import { getBillableApiKeys } from "@/db/tenant-providers"
import { fetchData } from "../utils"
import { createIsomorphicLogger } from '@/utils/logger/isomorphicLogger'
import { logError } from '@/utils/errorLogging'

const logger = createIsomorphicLogger('models.fetchModels')

export const fetchBillableKeys = async (supabase: SupabaseClient<Database>, tenant: Tables<"tenants">) => {
	let tenantBillableKeyMap:Record<string, string> = {}

  try {
		if (tenant.name != 'Aidon') {  //aidon cannot bill back itself. Illogical
			const tenantId = tenant.id
			const billableKeys = await getBillableApiKeys(supabase, tenantId)

			if (billableKeys && Object.entries(billableKeys).length > 0) {
				tenantBillableKeyMap = Object.keys(keyMap).reduce<Record<string, string>>((acc, provider) => {
					if (!!billableKeys[provider]) {
						acc[provider] = billableKeys[provider]
					}
					return acc
				}, {})
			}
		}
  }
	catch (error) {
    logger.warn("Error fetching hosted models: " + error)
  }

	return tenantBillableKeyMap
}

export const fetchHostedModels = async (supabase: SupabaseClient<Database>, profile: Tables<"profiles">,
		tenantId?: string) => {
	return await fetchTenantHostedModels(supabase, profile,
			tenantId ? await getTenantById(supabase, tenantId) : null)
}

export const fetchTenantHostedModels = async (supabase: SupabaseClient<Database>, profile: Tables<"profiles">,
		tenant: Tables<"tenants"> | null) => {
  try {
		const providers = await getAllDefinedProviders(supabase)
		const keys = await fetchData(`/keys`)

		let tenantKeyMap:Record<string, boolean> = {}
		let tenantBillableKeyMap:Record<string, boolean> = {}
		let tenantSharedKeyMap:Record<string, boolean> = {}

		if (tenant) {
			const tenantKeys = await fetchTenantKeys(supabase, profile, tenant)

			tenantKeyMap = Object.keys(keyMap).reduce<Record<string, boolean>>((acc, provider) => {
				acc[provider] = !!tenantKeys.tenantKeyMap[provider]
				return acc
			}, {})

			tenantSharedKeyMap = Object.keys(keyMap).reduce<Record<string, boolean>>((acc, provider) => {
				acc[provider] = !!tenantKeys.tenantSharedKeyMap[provider]
				return acc
			}, {})

			tenantBillableKeyMap = Object.keys(keyMap).reduce<Record<string, boolean>>((acc, provider) => {
				acc[provider] = !!tenantKeys.tenantBillableKeyMap[provider]
				return acc
			}, {})
		}

    let modelsToAdd: LLM[] = []

    for (const provider of providers) {
			const internName = provider.internal_name
      const providerKey: keyof typeof profile = `${provider.api_key_name}` as keyof typeof profile

      if (profile?.[providerKey] || keys.isUsingEnvKeyMap[internName] || tenantKeyMap[internName]) {
				const models = await getModelsByProviderId(supabase, provider.id)
				modelsToAdd = models.map(mapToLLM)
      }
    }

    return {
      envKeyMap: keys.isUsingEnvKeyMap,
			tenantKeyMap: tenantKeyMap,
			tenantBillableKeyMap: tenantBillableKeyMap,
			tenantSharedKeyMap: tenantSharedKeyMap,
      hostedModels: modelsToAdd
    }
  } catch (error) {
    logger.warn("Error fetching hosted models: " + error)
  }
}

export const fetchTenantKeys = async (supabase: SupabaseClient<Database>, profile: Tables<"profiles">, tenant: Tables<"tenants">) => {
	let tenantKeyMap:Record<string, string> = {}
	let tenantBillableKeyMap:Record<string, string> = {}
	let tenantSharedKeyMap:Record<string, string> = {}

  try {
		const tenantId = tenant.id
		const apiKeys = getApiKeys(tenant.metadata as Metadata)
		const validKeys = getValidEnvKeysRecords(apiKeys, VALID_ENV_KEYS)

		const tenantSharedKeys = await fetchData(`/keys/tenant`, {
			method: "POST",
			body: JSON.stringify({ tenantId })
		})

		tenantKeyMap = Object.keys(keyMap).reduce<Record<string, string>>((acc, provider) => {
			const key = keyMap[provider]

			if (key && validKeys[key]) {
				acc[provider] = validKeys[key]
			}
			return acc
		}, {})

		tenantSharedKeyMap = Object.keys(keyMap).reduce<Record<string, string>>((acc, provider) => {
			const key = keyMap[provider]

			if (key && tenantSharedKeys[key]) {
				acc[provider] = tenantSharedKeys[key]
			}
			return acc
		}, {})

		tenantBillableKeyMap = await fetchBillableKeys(supabase, tenant)
  } catch (error) {
    logger.warn("Error fetching hosted models: " + error)
  }

	return {
		tenantKeyMap: tenantKeyMap,
		tenantBillableKeyMap: tenantBillableKeyMap,
		tenantSharedKeyMap: tenantSharedKeyMap,
	}
}

export const fetchProviderHostedModels = async (supabase: SupabaseClient<Database>) => {
  try {
		const providers = await getAllDefinedProviders(supabase)
    const modelsToAdd: LLM[] = []

    for (const provider of providers) {
			const models = await getModelsByProviderId(supabase, provider.id)
			modelsToAdd.push(...models.map(mapToLLM))
    }

    return modelsToAdd
  } catch (error) {
    logger.warn("Error fetching hosted models: " + error)
  }
}

export const fetchOllamaModels = async () => {
  try {
    const response = await fetch(process.env.NEXT_PUBLIC_OLLAMA_URL + "/api/tags")

    if (!response.ok) {
      throw new Error(`Ollama server is not responding.`)
    }

    const data = await response.json()

    const localModels: LLM[] = data.models.map((model: any) => ({
      modelId: model.name as LLMID,
      modelName: model.name,
      provider: "ollama",
      hostedId: model.name,
      platformLink: "https://ollama.ai/library",
      imageInput: model.supports_images
    }))

    return localModels
  } catch (error) {
    logger.warn("Error fetching Ollama models: " + error)
  }
}

export const fetchOpenRouterModels = async (): Promise<OpenRouterLLM[]> => {
  try {
		const openRouterModels:OpenRouterLLM[] = await fetchData(`/models/openrouter/fetch`)
		logger.debug(`Loaded ${openRouterModels.length} OpenRouter models`)
		const usableOpenRouterModels = openRouterModels.filter(model => !model.modelId.endsWith(':free'))
		logger.debug(`Returning ${usableOpenRouterModels.length} usable OpenRouter models`)
    return usableOpenRouterModels
  } catch (error) {
		logError(logger, error, "Error fetching Open Router models: ")
		return []
  }
}